import {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {useAnalyticsDashboardContext} from 'insights/analytics-dashboards/details/AnalyticsDashboardDetailsContext';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {getAnalyticsQueryViewStateId} from 'insights/analytics-queries/AnalyticsQueryViewState';
import {convertGQLToRESTAnalyticsQuery} from 'insights/analytics-queries/data/AnalyticsQueryGQLRESTConverters';
import {
  resetLocalSQLQueryOverride,
  writeLocalSQLQueryOverride,
} from 'insights/analytics-queries/data/LocalSQLQueryOverride';
import {AnalyticsQuerySpecRequest} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import {
  runGraphQLAnalyticsQuery,
  runSubQuery,
} from 'insights/analytics-queries/run-query/RunAnalyticsQueryActions';
import runWarehouseQueryGlobalState from 'insights/analytics-queries/run-query/runWarehouseQueryGlobalState';
import {getExtraDataToTrackRunQuery} from 'insights/analytics-queries/run-query/trackRunQuery';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import syncParametersToSQLQueries from 'insights/parameters/data/syncParametersToSQLQueries';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {BenchlingState} from 'redux-stores/reduceApp';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';

type AnalyticsQuery = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>['analyticsQueries'][number];

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

interface Actions {
  runGraphQLAnalyticsQuery: typeof runGraphQLAnalyticsQuery;
  runSubQuery: typeof runSubQuery;
  updateAnalyticsQuery: (analyticsQueryId: string, data: AnalyticsQuerySpecRequest) => Promise<void>;
}

export interface UseSaveAndRunQueryInput {
  actions: Actions;
  analyticsDashboardAttrs: Pick<AnalyticsDashboard, 'createdAt' | 'creator' | 'modifiedAt'>;
  analyticsQuery: AnalyticsQuery;
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>;
  parameterValues: Array<AnalyticsParameterValue>;
}

interface UseSaveAndRunQueryOutput {
  draftSQLQuery: string;
  isResultsFromSubQuery: boolean;
  runQueryState: RunQueryState;
  saveAndRunQuery: (newSQLQuery?: string) => Promise<void>;
  setDraftSQLQuery: (newDraftQuery: string) => void;
  setSQLSubQuery: (newSQLSubQuery: string | null) => void;
  sqlSubQuery: string | null;
}

export default function useSaveAndRunQuery({
  actions,
  analyticsDashboardAttrs,
  analyticsQuery,
  parameterConfigs,
  parameterValues,
}: UseSaveAndRunQueryInput): UseSaveAndRunQueryOutput {
  const {analyticsDashboardViewStateId} = useAnalyticsDashboardContext();
  const analyticsQueryViewStateId = getAnalyticsQueryViewStateId(
    analyticsQuery.id,
    analyticsDashboardViewStateId
  );

  const [warehouseRunQueryState] =
    runWarehouseQueryGlobalState.useGlobalClientState(analyticsQueryViewStateId);
  // The only supported query-level draft attribute is the SQL query, because the user types it quickly and
  // we don't want to autosave on every keystroke. Our currently save model is to save on click ('Save and
  // run query' button).
  //
  // This is notably different from other attributes (name, visualization, etc.) which autosave on change.
  //
  // We may change the SQL query to autosave every few seconds, at which point we likely will no longer need
  // this draft state anymore: we can change it to call a Notebook-style action that writes the changes
  // locally and sends a server call every few seconds.
  const draftSQLQuery = useSelector(
    (state: BenchlingState) =>
      state.ui.insights.blockEditorUIs[analyticsQuery.id]?.sqlQueryOverride ?? analyticsQuery.sqlQuery
  );

  const setDraftSQLQuery = (newValue: string): void => {
    writeLocalSQLQueryOverride(analyticsQuery.id, newValue);
  };

  const setServerValue = (analyticsQueryId: string, newSQLQuery: string): Promise<void> =>
    actions.updateAnalyticsQuery(analyticsQueryId, {
      ...convertGQLToRESTAnalyticsQuery(analyticsQuery),
      sqlQuery: newSQLQuery,
    });

  const saveDraftSQLQuery = async (newValue?: string): Promise<void> => {
    newValue = newValue ?? draftSQLQuery;
    await setServerValue(analyticsQuery.id, newValue);
    resetLocalSQLQueryOverride(analyticsQuery.id);
  };

  const [sqlSubQuery, setSQLSubQuery] = useState<string | null>(null);
  const [sqlRunSubQueryState, setSQLRunSubQueryState] = useState<RunQueryState | null>(null);

  const runQueryState = useMemo(() => {
    if (sqlRunSubQueryState != null) {
      return sqlRunSubQueryState;
    } else {
      return warehouseRunQueryState;
    }
  }, [sqlRunSubQueryState, warehouseRunQueryState]);

  /**
   * Manages saving queries and running queries
   *
   * If a subquery exists, always run it
   * If a newSQLQuery or draftSQLQuery is different than the server's query, always save it
   * If a subquery does not exists and the query is not new, then run the query without saving
   */
  async function saveAndRunQuery(newSQLQuery: string = draftSQLQuery): Promise<void> {
    let shouldRunFullQuery = false;

    if (newSQLQuery !== analyticsQuery.sqlQuery) {
      await saveDraftSQLQuery(newSQLQuery);
    } else {
      shouldRunFullQuery = true;
    }

    if (sqlSubQuery != null) {
      // Sync parameters to the sub query. This handles the case where the user edits the query to add a new,
      // unsaved parameter and then runs the sub query which contains that parameter:
      //   sqlSubQuery = 'SELECT {{unsaved_parameter}}'
      //   parameterConfigs = []
      //     (using this errors because it's missing `unsaved_parameter`)
      //   subQueryParameterConfigs = [{systemName: 'unsaved_parameter'}]
      //     (from the sync; using this does not error)
      const {parameterConfigs: subQueryParameterConfigs, parameterValues: subQueryParameterValues} =
        syncParametersToSQLQueries([sqlSubQuery], parameterConfigs, parameterValues);
      actions.runSubQuery(
        analyticsQuery,
        sqlSubQuery,
        runQueryState,
        subQueryParameterConfigs,
        subQueryParameterValues,
        setSQLRunSubQueryState,
        getExtraDataToTrackRunQuery(analyticsDashboardViewStateId, true)
      );
    } else {
      // If we set sqlRunSubQueryState to null at the same time as setting sqlSubQuery to null, the results
      // of the sub queries would be removed whenever the user unselects their sub query, which is undesirable.
      // Instead, we set sqlRunSubQueryState to null the next time a full query is ran.
      setSQLRunSubQueryState(null);

      if (shouldRunFullQuery) {
        actions.runGraphQLAnalyticsQuery(
          analyticsQuery,
          getAnalyticsQueryViewStateId(analyticsQuery.id, analyticsDashboardViewStateId),
          parameterConfigs,
          parameterValues,
          warehouseRunQueryState,
          getExtraDataToTrackRunQuery(analyticsDashboardViewStateId, true)
        );
      }
    }
  }

  return {
    draftSQLQuery,
    isResultsFromSubQuery: sqlRunSubQueryState != null,
    setDraftSQLQuery,
    setSQLSubQuery,
    runQueryState,
    saveAndRunQuery,
    sqlSubQuery,
  };
}
